import {Link, useLocation} from '@tanstack/react-router'
import {CalendarDays, LayoutGrid, type LucideIcon, Menu} from 'lucide-react'
import {useSidebar} from '@/components/ui/sidebar'
import {cn} from '@/lib/utils'

/**
 * Mobile-only bottom tab bar. Rendered in-flow at the bottom of the layout
 * column (not fixed) so page content can never hide underneath it. The
 * env(safe-area-inset-bottom) padding clears the iPhone home indicator
 * (requires viewport-fit=cover in index.html). "More" opens the existing
 * sidebar Sheet, which holds the admin links, account footer, and dev tools.
 */
export function MobileTabBar() {
	const {setOpenMobile} = useSidebar()

	return (
		<nav
			aria-label='Primary'
			className='flex shrink-0 items-stretch border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden'
		>
			<TabLink to='/today' label='Today' Icon={CalendarDays} />
			<TabLink to='/board' label='Board' Icon={LayoutGrid} />
			<button
				type='button'
				onClick={() => setOpenMobile(true)}
				className='flex h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-muted-foreground'
			>
				<Menu className='size-5' />
				<span className='text-[10px] font-medium leading-none'>More</span>
			</button>
		</nav>
	)
}

function TabLink({
	to,
	label,
	Icon,
}: {
	to: string
	label: string
	Icon: LucideIcon
}) {
	const isActive = useLocation({select: (s) => s.pathname === to})

	return (
		<Link
			to={to}
			aria-current={isActive ? 'page' : undefined}
			className={cn(
				'flex h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5',
				isActive ? 'text-(--mat-primary)' : 'text-muted-foreground',
			)}
		>
			<Icon className='size-5' />
			<span className='text-[10px] font-medium leading-none'>{label}</span>
		</Link>
	)
}
